import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Grid, Paper, TextField, Button,
    Drawer, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, LinearProgress, Badge, Tooltip,
    CircularProgress, Select, MenuItem, InputLabel, FormControl,
    InputAdornment, Pagination, Typography, IconButton, Skeleton, Alert
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// Custom Components
import SmartProductCard from '../components/SmartProductCard';
import useCategoryTaxonomy from '../components/useCategoryTaxonomy';

// Styles
import './SmartSearchPage.css';

// Auth & Config
import { useAuth } from '../AuthProvider';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';
const urlconfig = require('../config/urlconfig');

// --- OPTIMIZATION 1: Memoize Product Card to prevent re-renders ---
const MemoizedProductCard = React.memo(SmartProductCard);

// Cards per page; the first load and every later search must use the same size, or page 2
// wouldn't start where page 1 ended
const PAGE_SIZE = 15;

// --- SEARCH SESSION: survives opening a product and coming back (per browser tab) ---
const SEARCH_STATE_KEY = 'smartSearch.state';
const EMPTY_FILTERS = { brand: '', category: '', subcategory: '', minPrice: '', maxPrice: '' };
const loadSavedSearch = () => {
    try {
        return JSON.parse(sessionStorage.getItem(SEARCH_STATE_KEY)) || null;
    } catch {
        return null;
    }
};
const saveSearch = (state) => {
    try {
        sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(state));
    } catch { /* storage full or blocked — page still works, just won't restore */ }
};

const SmartSearchPage = () => {
    const { account } = useAuth();
    const { instance } = useMsal();
    const [saved] = useState(loadSavedSearch);
    // Saved results are only restored if a search actually completed: leaving before the first
    // search returned (or after it failed) must not bring back an empty "No products found" page
    const [restored] = useState(() => (saved?.searchDone ? saved : null));

    // --- SEARCH STATE ---
    const [query, setQuery] = useState(saved?.query ?? '');
    const [results, setResults] = useState(restored?.results ?? []);
    const [loading, setLoading] = useState(!restored); // Start loading immediately unless restoring
    const [searchDone, setSearchDone] = useState(!!restored);

    // --- PAGINATION STATE ---
    const [page, setPage] = useState(restored?.page ?? 1);
    const [totalPages, setTotalPages] = useState(restored?.totalPages ?? 1);
    const [totalElements, setTotalElements] = useState(restored?.totalElements ?? 0);

    // Filters
    const [filters, setFilters] = useState(saved?.filters ?? EMPTY_FILTERS);

    // The search the shown results belong to. The search box and filters can hold edits that were
    // never submitted, so page changes and the empty-state text use this instead.
    const [lastSearch, setLastSearch] = useState(
        restored?.lastSearch ?? { query: restored?.query ?? '', filters: restored?.filters ?? EMPTY_FILTERS }
    );
    const [searchError, setSearchError] = useState(null);
    const { categories, brands, subcategories, ensureSubcategoriesFor } = useCategoryTaxonomy();

    // --- GIFT BOX STATE ---
    const [isBoxOpen, setIsBoxOpen] = useState(false);
    const [giftBox, setGiftBox] = useState(saved?.giftBox ?? []);
    const [budget, setBudget] = useState(saved?.budget ?? 2000);
    const [aiLoading, setAiLoading] = useState(false);

    // --- PERSIST / RESTORE SEARCH SESSION ---
    const scrollRef = useRef(null);
    const scrollTopRef = useRef(restored?.scrollTop ?? 0);

    useEffect(() => {
        saveSearch({ query, filters, lastSearch, page, totalPages, totalElements, results, searchDone, giftBox, budget, scrollTop: scrollTopRef.current });
    }, [query, filters, lastSearch, page, totalPages, totalElements, results, searchDone, giftBox, budget]);

    // Scroll position changes too often to save on every event; write it once when leaving the page
    useEffect(() => () => {
        const state = loadSavedSearch();
        if (state) saveSearch({ ...state, scrollTop: scrollTopRef.current });
    }, []);

    useLayoutEffect(() => {
        if (restored && scrollRef.current) scrollRef.current.scrollTop = restored.scrollTop || 0;
    }, [restored]);

    // --- HELPER: API WRAPPER ---
    const fetchWithToken = useCallback(async (url) => {
        const token = await getApiToken(instance, account);
        return fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
    }, [account, instance]);

    // Re-scope the Subcategory list to the selected category — the dropdown itself is disabled
    // until a category is picked, so there's nothing to show (and nothing to fetch) otherwise.
    useEffect(() => {
        ensureSubcategoriesFor(filters.category ? [filters.category] : []);
    }, [ensureSubcategoriesFor, filters.category]);

    // --- SEARCH ---
    // Every search goes through here: the first load, Apply/Enter, Clear and page changes.
    // Only the newest request may update the page, so a slow earlier response (e.g. after clicking
    // through pages quickly) can't overwrite a newer one.
    const latestRequestRef = useRef(0);
    const runSearch = useCallback(async (pageNo, criteria) => {
        if (!account) return;
        const requestId = ++latestRequestRef.current;
        const isLatest = () => requestId === latestRequestRef.current;
        setLoading(true);
        setSearchError(null);
        try {
            const f = criteria.filters;
            // The page count can overshoot on very large catalogs; if a page comes back empty,
            // step back to the last page that has results
            for (let p = pageNo; p >= 1; p--) {
                const params = new URLSearchParams({
                    query: criteria.query,
                    page: p - 1,
                    size: PAGE_SIZE,
                    ...(f.brand && { brand: f.brand }),
                    ...(f.category && { category: f.category }),
                    ...(f.subcategory && { subcategory: f.subcategory }),
                    ...(f.minPrice && { minPp: f.minPrice }),
                    ...(f.maxPrice && { maxPp: f.maxPrice })
                });
                const response = await fetchWithToken(`${urlconfig.backend_api_base_url}/api/v2/smart-search?${params.toString()}`);
                if (!response.ok) throw new Error(`Search failed (${response.status})`);
                const data = await response.json();
                if (!isLatest()) return;
                const content = data.content || [];
                if (content.length === 0 && p > 1) continue;

                setResults(content);
                setTotalPages(Math.max(data.totalPages || 1, 1));
                setTotalElements(data.totalElements || 0);
                setPage(p);
                setLastSearch(criteria);
                setSearchDone(true);
                return;
            }
        } catch (error) {
            console.error("Search failed", error);
            // Keep the previous results on screen; the banner says this search didn't go through
            if (isLatest()) setSearchError("Search failed. Please check your connection and try again.");
        } finally {
            if (isLatest()) setLoading(false);
        }
    }, [account, fetchWithToken]);

    // --- INITIAL SEARCH RESULTS --- (categories/brands/subcategories come from useCategoryTaxonomy)
    useEffect(() => {
        // Coming back to a completed search: results are already restored
        if (!account || restored) return;
        runSearch(1, { query: '', filters: EMPTY_FILTERS });
    }, [account, restored, runSearch]); // Run once on mount

    // --- GIFT BOX ACTIONS ---
    const addToBox = useCallback((product) => {
        setGiftBox((prev) => [...prev, { ...product, uniqueId: Date.now() }]);
        setIsBoxOpen(true);
    }, []);

    const removeFromBox = useCallback((uniqueId) => {
        setGiftBox((prev) => prev.filter(item => item.uniqueId !== uniqueId));
    }, []);

    const handleAiAutoFill = async () => {
        if (!account) return;
        setAiLoading(true);
        try {
            const token = await getApiToken(instance, account);
            const response = await fetch(
                `${urlconfig.backend_api_base_url}/api/v2/gift-curator/generate?budget=${budget}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const bundles = await response.json();
            if (bundles && bundles.length > 0) {
                const newItems = bundles[0].map(item => ({ ...item, uniqueId: Date.now() + Math.random() }));
                setGiftBox(newItems);
                setIsBoxOpen(true);
            } else {
                alert("AI couldn't find a match. Try a higher budget!");
            }
        } catch (error) { console.error("AI failed", error); }
        finally { setAiLoading(false); }
    };

    const totalCost = useMemo(() => giftBox.reduce((sum, item) => sum + (item.ecomMrp || item.lastPurchasePrice || 0), 0), [giftBox]);
    const progress = Math.min((totalCost / budget) * 100, 100);

    const handleClear = () => {
        setQuery('');
        setFilters(EMPTY_FILTERS);
        scrollTopRef.current = 0;
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        runSearch(1, { query: '', filters: EMPTY_FILTERS });
    };

    const handleSearch = () => runSearch(1, { query, filters });

    const handlePageChange = (event, value) => {
        runSearch(value, lastSearch);
        document.getElementById('search-results-top')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <Box className="smart-search-page" sx={{ display: 'flex', height: 'calc(100vh - 80px)', bgcolor: '#f8fafc' }}>
            <Box
                ref={scrollRef}
                onScroll={(e) => { scrollTopRef.current = e.currentTarget.scrollTop; }}
                sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}
            >
                <div id="search-results-top" />

                <div className="smart-search-header">
                    <div>
                        <h1 className="smart-search-title">Smart Search</h1>
                        <p className="smart-search-subtitle">Past purchases first, then the product catalog</p>
                    </div>
                    {!loading && totalElements > 0 && (
                        <span className="smart-search-count">
                            {totalElements.toLocaleString('en-IN')} {totalElements === 1 ? 'product' : 'products'}
                        </span>
                    )}
                </div>

                <Paper
                    elevation={0}
                    sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md>
                            <TextField
                                fullWidth label="Search" placeholder="Product name, e.g. t-shirt" variant="outlined" size="small"
                                value={query} onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={filters.category}
                                    label="Category"
                                    onChange={(e) => setFilters({...filters, category: e.target.value, subcategory: ''})}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {categories.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Subcategory</InputLabel>
                                <Select
                                    value={filters.subcategory}
                                    label="Subcategory"
                                    onChange={(e) => setFilters({...filters, subcategory: e.target.value})}
                                    disabled={!filters.category}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {subcategories.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Brand</InputLabel>
                                <Select value={filters.brand} label="Brand" onChange={(e) => setFilters({...filters, brand: e.target.value})}>
                                    <MenuItem value="">All</MenuItem>
                                    {brands.map(b => <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3} md={1}>
                            <TextField
                                fullWidth label="Min" variant="outlined" size="small" type="number"
                                value={filters.minPrice} onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={3} md={1}>
                            <TextField
                                fullWidth label="Max" variant="outlined" size="small" type="number"
                                value={filters.maxPrice} onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={4} md="auto">
                            <Button variant="contained" disableElevation fullWidth onClick={handleSearch} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Apply</Button>
                        </Grid>
                        <Grid item xs={4} md="auto">
                            <Button variant="text" fullWidth onClick={handleClear} disabled={loading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Clear</Button>
                        </Grid>
                        <Grid item xs={4} md="auto">
                            <Tooltip title={isBoxOpen ? "Hide Box" : "View Gift Box"}>
                                <span>
                                    <Button
                                        variant="outlined" color="warning" fullWidth
                                        onClick={() => setIsBoxOpen(!isBoxOpen)}
                                        sx={{ minWidth: '40px', px: 1 }}
                                        disabled={true}
                                    >
                                        <Badge badgeContent={giftBox.length} color="primary">
                                            <ShoppingBagIcon />
                                        </Badge>
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>

                {searchError && !loading && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={() => runSearch(page, lastSearch)}>
                                Retry
                            </Button>
                        }
                    >
                        {searchError}
                    </Alert>
                )}

                {loading ? (
                    <div className="smart-products-grid" aria-busy="true">
                        {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                            <div key={i} className="smart-skeleton-card">
                                <Skeleton variant="rectangular" height={190} />
                                <Box sx={{ p: 2 }}>
                                    <Skeleton width="40%" height={16} />
                                    <Skeleton width="90%" height={22} />
                                    <Skeleton width="60%" height={22} />
                                    <Skeleton variant="rounded" height={52} sx={{ mt: 1.5, borderRadius: 2.5 }} />
                                    <Skeleton variant="rounded" height={38} sx={{ mt: 1.5, borderRadius: 2.5 }} />
                                </Box>
                            </div>
                        ))}
                    </div>
                ) : results.length === 0 ? (searchError ? null : (
                    <div className="smart-empty">
                        <SearchOffIcon className="smart-empty-icon" />
                        <h2>No products found</h2>
                        <p>{lastSearch.query ? <>Nothing matches “{lastSearch.query}” with the current filters.</> : 'Try a different search or filters.'}</p>
                        <Button variant="outlined" onClick={handleClear} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                            Clear search
                        </Button>
                    </div>
                )) : (
                    <>
                        <div className="smart-products-grid">
                            {results.map((product) => (
                                <MemoizedProductCard
                                    key={product.subItemId != null ? `oms-${product.subItemId}` : `catalog-${product.ecomProductId}`}
                                    product={product}
                                    onAdd={addToBox} // Assuming SmartProductCard takes this prop now
                                />
                            ))}
                        </div>

                        {results.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                                <Pagination
                                    count={totalPages} page={page} onChange={handlePageChange}
                                    color="primary" shape="rounded" showFirstButton showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <Drawer
                anchor="right" open={isBoxOpen} onClose={() => setIsBoxOpen(false)}
                variant="persistent"
                sx={{
                    width: isBoxOpen ? 350 : 0, flexShrink: 0,
                    '& .MuiDrawer-paper': { width: 350, boxSizing: 'border-box', borderLeft: '1px solid #e0e0e0' },
                }}
            >
                {/* --- OPTIMIZATION 3: Lazy Render Content --- */}
                {isBoxOpen && (
                    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">🎁 Gift Box</Typography>
                            <IconButton onClick={() => setIsBoxOpen(false)}><CloseIcon /></IconButton>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fffdeb' }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    size="small" type="number" label="Budget"
                                    value={budget} onChange={(e) => setBudget(Number(e.target.value))}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                />
                                <Tooltip title="AI Auto-Fill">
                                    <Button variant="contained" color="warning" onClick={handleAiAutoFill} disabled={aiLoading}>
                                        {aiLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                                    </Button>
                                </Tooltip>
                            </Box>
                            <LinearProgress variant="determinate" value={progress > 100 ? 100 : progress} sx={{ height: 8, borderRadius: 4 }} />
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                                ₹{totalCost.toLocaleString()} / ₹{budget.toLocaleString()}
                            </Typography>
                        </Paper>
                        <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            {giftBox.map((item) => (
                                <ListItem key={item.uniqueId} divider>
                                    <ListItemAvatar>
                                        <Avatar src={item.ecomThumbnailUrl} variant="rounded"><ShoppingBagIcon /></Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={item.productName} primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                                        secondary={`₹${item.ecomMrp || 0}`}
                                    />
                                    <IconButton size="small" color="error" onClick={() => removeFromBox(item.uniqueId)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </Drawer>
        </Box>
    );
};

export default SmartSearchPage;